import { useState } from "react";
import { toast } from "sonner";
import { API, authFetch, useAuth } from "@/App";
import { RefreshCw, Smartphone } from "lucide-react";
import { Button } from "@/components/ui/button";

/**
 * <SyncNowButton />
 *
 * Triggers a WebSocket `SETTINGS_UPDATED` broadcast — causes the Pravaah mobile app
 * to re-fetch config (and any live subscribed modules). Use on every admin page that
 * edits data the mobile app consumes.
 *
 * Props:
 *   entity       string  "products" | "dealers" | "schemes" | "orders" | ...
 *                        shown in the toast ("Synced products to mobile")
 *   className    string
 *   compact      boolean if true, renders just the icon (for tight toolbars)
 */
export const SyncNowButton = ({ entity = "changes", className = "", compact = false }) => {
  const { user } = useAuth();
  const [syncing, setSyncing] = useState(false);

  // Only show to admin-tier roles
  const allowed = ["SUPER_ADMIN", "FACTORY_ADMIN", "SALES_ADMIN"].includes(user?.role);
  if (!allowed) return null;

  const handleSync = async () => {
    setSyncing(true);
    try {
      const res = await authFetch(`${API}/v1/settings/sync`, {
        method: "POST",
        headers: { "Content-Type": "application/json" },
        body: JSON.stringify({ entity }),
      });
      const data = await res.json();
      if (data.success) {
        toast.success(`✓ Synced ${entity} to mobile app`, {
          description: "All field users will see the latest data instantly.",
          duration: 3500,
        });
      } else {
        toast.error(data.detail || "Sync failed");
      }
    } catch {
      toast.error("Network error during sync");
    } finally {
      setSyncing(false);
    }
  };

  if (compact) {
    return (
      <Button
        variant="outline"
        size="sm"
        onClick={handleSync}
        disabled={syncing}
        className={`h-9 w-9 p-0 ${className}`}
        title={`Sync ${entity} to mobile app`}
        data-testid="sync-now-btn"
      >
        <Smartphone className={`w-4 h-4 ${syncing ? "animate-pulse text-[#8B1A1A]" : ""}`} />
      </Button>
    );
  }

  return (
    <Button
      variant="outline"
      size="sm"
      onClick={handleSync}
      disabled={syncing}
      className={`gap-2 border-[#8B1A1A] text-[#8B1A1A] hover:bg-[#8B1A1A] hover:text-white ${className}`}
      data-testid="sync-now-btn"
    >
      {syncing ? (
        <RefreshCw className="w-4 h-4 animate-spin" />
      ) : (
        <Smartphone className="w-4 h-4" />
      )}
      {syncing ? "Syncing…" : "Sync Now"}
    </Button>
  );
};

export default SyncNowButton;
